import React, { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Button, Row } from 'reactstrap'
import AutoTable from 'components/AutoTable'
import { IsRosteredBadge } from 'components/IsRosteredBadge/IsRosteredBadge'
import UserActions from 'components/UserActions'
import { getSitesApplicableRoles } from 'lib/util'
import { ManualBadge } from 'components/ManualBadge/ManualBadge'
import AddUserModal from 'components/AddUserModal'
import {
  AddUserButton,
  ButtonsContainer,
  InviteAndRemindContainer,
  UserSummary,
  UserGridStatusColContainer,
  UserCount,
  UserCountPrefix,
} from './component.styles'
import SendInvitesDialog from 'components/SendInvitesDialog'
import { Site, SiteUser } from 'types'

interface UserMetaInfo {
  all?: number
  pending?: number
  admin?: number
  leaderTeam?: number
  teacher?: number
  [key: string]: number | undefined
}

interface SiteUsersProps {
  site?: Site
  siteUsers?: SiteUser[]
  handleCancelUserAccess: (user: SiteUser) => void
  handleGrantNewRole: (user: SiteUser, newRole: string) => void
  addBulkUsersResponse?: {
    newSiteUsers?: SiteUser[]
    failedSiteUsers?: SiteUser[]
    invalidSiteUsers?: SiteUser[]
    sendEmail?: boolean
  }
  error?: Error
  isBulkOperationCompleted?: boolean
  handleOpenAddUsersModal: () => void
  handleCloseAddUsersModal: () => void
  isAddUserModalOpen: boolean
  remindSiteUser: (siteUser: SiteUser) => void
  remindSiteUsers: (
    siteId: number,
    emailFilters?: {
      pendingUser?: boolean
      roles?: string[]
    },
  ) => void
  selectedSite?: Site
  handleSubmitAddUsers: (
    emails: string[],
    role: string,
    sendNotification: boolean,
    inputMode: string,
  ) => Promise<void>
}

const SiteUsers: React.FC<SiteUsersProps> = ({
  addBulkUsersResponse,
  error,
  handleCancelUserAccess,
  handleCloseAddUsersModal,
  handleGrantNewRole,
  handleOpenAddUsersModal,
  handleSubmitAddUsers,
  isAddUserModalOpen,
  isBulkOperationCompleted,
  remindSiteUser,
  remindSiteUsers,
  selectedSite,
  site,
  siteUsers,
}) => {
  const [sendInvitesModalOpen, setInvitesModalOpen] = useState(false)

  const activeUsers = useMemo(
    () => siteUsers?.filter(user => user.isActive) || [],
    [siteUsers],
  )

  const userMetaInfo = useMemo(() => {
    const counts: UserMetaInfo = {
      all: activeUsers.length,
      admin: 0,
      leaderTeam: 0,
      teacher: 0,
      pending: 0,
    }

    activeUsers.forEach(user => {
      const role = user.roles[0]
      if (role === 'Admin') counts.admin = (counts.admin || 0) + 1
      if (role === 'Leader Team')
        counts.leaderTeam = (counts.leaderTeam || 0) + 1
      if (role === 'Teacher') counts.teacher = (counts.teacher || 0) + 1
      if (!user.userId) counts.pending = (counts.pending || 0) + 1
    })

    return Object.fromEntries(
      Object.entries(counts).filter(([, value]) => (value || 0) > 0),
    ) as UserMetaInfo
  }, [activeUsers])

  const formatUserGroup = (status: string): string => {
    const roleCounts = new Map<string, number>()
    activeUsers
      .filter(user => user.status === status)
      .forEach(user => {
        const role = user.roles[0]
        roleCounts.set(role, (roleCounts.get(role) || 0) + 1)
      })

    return Array.from(roleCounts.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([role, count]) => {
        const plural =
          role !== 'Leader Team' && role !== 'Staff' && count > 1 ? 's' : ''
        return `${count} ${role}${plural}`
      })
      .join(', ')
  }

  const activeUserString = formatUserGroup('Active')
  const invitedUserString = formatUserGroup('Invited')

  const handleSendInvitesConfirm = ({
    all,
    pending,
    ...roles
  }: UserMetaInfo) => {
    if (!siteUsers?.length) {
      setInvitesModalOpen(false)
      return
    }

    const siteId = siteUsers[0].siteId
    const sendInvObj: { pendingUser?: boolean; roles?: string[] } = {
      pendingUser: !!pending,
    }

    if (!all && !pending && Object.values(roles).every(role => !role)) {
      setInvitesModalOpen(false)
      return
    }

    if (!all && !pending) {
      const sendToRoles = Object.entries(roles)
        .filter(([, value]) => value)
        .map(([role]) =>
          role === 'leaderTeam'
            ? 'Leader Team'
            : `${role.charAt(0).toUpperCase()}${role.slice(1)}`,
        )
      if (sendToRoles.length > 0) {
        sendInvObj.roles = sendToRoles
      }
    }

    remindSiteUsers(siteId, sendInvObj)
    setInvitesModalOpen(false)
  }

  const columns = [
    {
      title: 'Name',
      fieldValue: (row: SiteUser) => {
        const name = `${row.firstName || ''} ${row.lastName || ''}`.trim()
        return name || ''
      },
    },
    {
      title: 'Email',
      fieldValue: (row: SiteUser) => {
        if (row.inviteEmail && row.userId) {
          return (
            <Link to={`/css-admin/users/${row.inviteEmail}/${row.userId}`}>
              {row.inviteEmail}
            </Link>
          )
        }
        return row.inviteEmail
      },
      sortValue: 'inviteEmail',
      filterValue: 'inviteEmail',
    },
    {
      title: 'Role',
      fieldValue: 'roles',
    },
    {
      title: 'Account Status',
      fieldValue: (row: SiteUser) => (
        <UserGridStatusColContainer>
          <span>{row.status}</span>
          <ManualBadge isRostered={row.isActive} />
          <IsRosteredBadge
            isRostered={row.isRostered}
            summaryView={undefined}
          />
        </UserGridStatusColContainer>
      ),
      sortValue: 'status',
    },
    {
      title: 'Last Logged In',
      fieldValue: (row: SiteUser) =>
        row.lastLoggedInAt
          ? new Date(row.lastLoggedInAt).toLocaleDateString('en-us', {
              weekday: 'long',
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })
          : '',
      sortValue: 'lastLoggedInAt',
    },
    {
      title: '',
      sort: false,
      fieldValue: (row: SiteUser) => (
        <div style={{ whiteSpace: 'nowrap' }}>
          <UserActions
            applicableRoles={getSitesApplicableRoles(site)}
            handleCancelUserAccess={handleCancelUserAccess}
            handleGrantNewRole={handleGrantNewRole}
            remindSiteUser={remindSiteUser}
            user={{ ...row, preferences: row.preferences || [] }}
          />
        </div>
      ),
      cellClassName: 'text-center',
    },
  ]

  return (
    <>
      <InviteAndRemindContainer>
        <UserSummary>
          {activeUsers.length > 0 && (
            <UserCount>
              <UserCountPrefix>
                {activeUsers.length} active user
                {activeUsers.length > 1 ? 's' : ''}
              </UserCountPrefix>{' '}
              ({activeUserString})
            </UserCount>
          )}
          {userMetaInfo.pending && userMetaInfo.pending > 0 && (
            <UserCount>
              <UserCountPrefix>
                {userMetaInfo.pending} user{userMetaInfo.pending > 1 ? 's' : ''}{' '}
                without accounts
              </UserCountPrefix>{' '}
              ({invitedUserString})
            </UserCount>
          )}
        </UserSummary>
        <ButtonsContainer>
          <Button
            color="primary"
            onClick={() => setInvitesModalOpen(true)}
            size="sm"
          >
            <i className="fa fa-envelope" /> Send Invites
          </Button>
          <AddUserButton
            color="primary"
            onClick={handleOpenAddUsersModal}
            size="sm"
          >
            <i className="fa fa-user-plus" /> Add Users
          </AddUserButton>
        </ButtonsContainer>
      </InviteAndRemindContainer>
      <Row>
        <AutoTable
          borderless
          columns={columns}
          filter
          headThemeLight
          rows={siteUsers || []}
          sort
        />
      </Row>
      <SendInvitesDialog
        isOpen={sendInvitesModalOpen}
        onCancel={() => setInvitesModalOpen(false)}
        onConfirm={handleSendInvitesConfirm}
        showIcon={true}
        userMetaInfo={userMetaInfo}
      />
      <AddUserModal
        addBulkUsersResponse={addBulkUsersResponse}
        isBulkOperationCompleted={isBulkOperationCompleted}
        closeModal={handleCloseAddUsersModal}
        error={error}
        isModalOpen={isAddUserModalOpen}
        handleSubmitAddUsers={handleSubmitAddUsers}
        selectedSite={selectedSite}
        handleOpenAddUsersModal={handleOpenAddUsersModal}
      />
    </>
  )
}

export default SiteUsers
